/**
 * Centralized Error Messages with Localization Support
 * Provides consistent error messages in multiple languages
 */

const errorMessages = {
  en: {
    // Authentication Errors
    loginFailed: 'Login failed. Please check your credentials.',
    registrationFailed: 'Registration failed. Please try again.',
    authenticationRequired: 'Authentication required. Please login.',
    accessDenied: 'Access denied. You don\'t have permission for this action.',
    sessionExpired: 'Your session has expired. Please login again.',
    
    // Validation Errors
    requiredField: 'This field is required.',
    invalidEmail: 'Please enter a valid email address.',
    passwordMismatch: 'Passwords do not match.',
    invalidFileFormat: 'Invalid file format. Please check the file type.',
    fileTooLarge: 'File is too large. Please choose a smaller file.',
    
    // Network Errors
    networkError: 'Network connection failed. Please check your internet connection.',
    serverError: 'Server error occurred. Please try again later.',
    requestTimeout: 'Request timeout. Please try again.',
    serviceUnavailable: 'Service is temporarily unavailable. Please try again later.',
    
    // File Processing Errors
    fileUploadFailed: 'File upload failed. Please try again.',
    fileProcessingFailed: 'File processing failed. Please check the file format.',
    downloadFailed: 'Download failed. Please try again.',
    
    // Workflow Errors
    workflowSaveFailed: 'Failed to save workflow. Please try again.',
    workflowExecutionFailed: 'Workflow execution failed. Please check your configuration.',
    invalidWorkflowConfiguration: 'Invalid workflow configuration.',
    
    // General Errors
    unexpectedError: 'An unexpected error occurred. Please try again.',
    operationFailed: 'Operation failed. Please try again.',
    dataLoadFailed: 'Failed to load data. Please refresh the page.',
    
    // Form Validation
    usernameRequired: 'Username is required.',
    passwordRequired: 'Password is required.',
    emailRequired: 'Email address is required.',
    nameRequired: 'Name is required.',
    
    // Cloud Storage
    folderCreationFailed: 'Failed to create folder. Please try again.',
    itemNotFound: 'Item not found.',
    permissionDenied: 'Permission denied for this operation.',
    
    // Vectorization
    vectorizationFailed: 'File vectorization failed. Please try again.',
    vectorizationInProgress: 'Vectorization is already in progress for this file.',
    invalidSourceColumn: 'Invalid source column selected.',
    
    // UI Messages
    pleaseWait: 'Please wait...',
    loading: 'Loading...',
    processing: 'Processing...',
    success: 'Operation completed successfully.',
    
    // Retry Messages
    retryAction: 'Please try again.',
    retryLater: 'Please try again later.',
    refreshPage: 'Please refresh the page and try again.'
  },
  
  zh: {
    // Authentication Errors
    loginFailed: '登录失败，请检查您的凭据。',
    registrationFailed: '注册失败，请重试。',
    authenticationRequired: '需要身份验证，请登录。',
    accessDenied: '访问被拒绝，您没有执行此操作的权限。',
    sessionExpired: '您的会话已过期，请重新登录。',
    
    // Validation Errors
    requiredField: '此字段为必填项。',
    invalidEmail: '请输入有效的电子邮件地址。',
    passwordMismatch: '密码不匹配。',
    invalidFileFormat: '无效的文件格式，请检查文件类型。',
    fileTooLarge: '文件太大，请选择较小的文件。',
    
    // Network Errors
    networkError: '网络连接失败，请检查您的互联网连接。',
    serverError: '服务器错误，请稍后重试。',
    requestTimeout: '请求超时，请重试。',
    serviceUnavailable: '服务暂时不可用，请稍后重试。',
    
    // File Processing Errors
    fileUploadFailed: '文件上传失败，请重试。',
    fileProcessingFailed: '文件处理失败，请检查文件格式。',
    downloadFailed: '下载失败，请重试。',
    
    // Workflow Errors
    workflowSaveFailed: '保存工作流失败，请重试。',
    workflowExecutionFailed: '工作流执行失败，请检查您的配置。',
    invalidWorkflowConfiguration: '无效的工作流配置。',
    
    // General Errors
    unexpectedError: '发生意外错误，请重试。',
    operationFailed: '操作失败，请重试。',
    dataLoadFailed: '加载数据失败，请刷新页面。',
    
    // Form Validation
    usernameRequired: '用户名为必填项。',
    passwordRequired: '密码为必填项。',
    emailRequired: '电子邮件地址为必填项。',
    nameRequired: '名称为必填项。',
    
    // Cloud Storage
    folderCreationFailed: '创建文件夹失败，请重试。',
    itemNotFound: '找不到项目。',
    permissionDenied: '此操作被拒绝权限。',
    
    // Vectorization
    vectorizationFailed: '文件向量化失败，请重试。',
    vectorizationInProgress: '此文件的向量化已在进行中。',
    invalidSourceColumn: '选择的源列无效。',
    
    // UI Messages
    pleaseWait: '请稍候...',
    loading: '加载中...',
    processing: '处理中...',
    success: '操作成功完成。',
    
    // Retry Messages
    retryAction: '请重试。',
    retryLater: '请稍后重试。',
    refreshPage: '请刷新页面并重试。'
  }
};

// Default language
let currentLanguage = 'en';

/**
 * Set the current language for error messages
 * @param {string} language - Language code ('en' or 'zh')
 */
export const setErrorLanguage = (language) => {
  if (errorMessages[language]) {
    currentLanguage = language;
  }
};

/**
 * Get error message by key
 * @param {string} key - Message key
 * @param {string} fallback - Fallback message if key not found
 * @param {string} language - Override current language
 * @returns {string} Localized error message
 */
export const getErrorMessage = (key, fallback = null, language = null) => {
  const lang = language || currentLanguage;
  const messages = errorMessages[lang] || errorMessages.en;
  return messages[key] || fallback || `Error: ${key}`;
};

/**
 * Get error message with parameters
 * @param {string} key - Message key
 * @param {object} params - Parameters to replace in message
 * @param {string} language - Override current language
 * @returns {string} Localized error message with parameters
 */
export const getErrorMessageWithParams = (key, params = {}, language = null) => {
  let message = getErrorMessage(key, null, language);
  
  // Replace parameters in message
  Object.keys(params).forEach(param => {
    message = message.replace(`{${param}}`, params[param]);
  });
  
  return message;
};

/**
 * Map error codes to user-friendly messages
 * @param {string} errorCode - Backend error code
 * @param {string} defaultMessage - Default message if code not mapped
 * @param {string} language - Override current language
 * @returns {string} User-friendly error message
 */
export const mapErrorCodeToMessage = (errorCode, defaultMessage = null, language = null) => {
  const codeMapping = {
    // Authentication codes
    'AUTH_001': 'authenticationRequired',
    'AUTH_002': 'loginFailed',
    'AUTH_003': 'accessDenied',
    'AUTH_004': 'sessionExpired',
    
    // Validation codes
    'VAL_001': 'requiredField',
    'VAL_002': 'invalidFileFormat',
    'VAL_003': 'invalidEmail',
    
    // File processing codes
    'FILE_001': 'fileUploadFailed',
    'FILE_002': 'fileProcessingFailed',
    'FILE_003': 'invalidFileFormat',
    'FILE_004': 'fileTooLarge',
    
    // Workflow codes
    'WF_001': 'workflowSaveFailed',
    'WF_002': 'workflowExecutionFailed',
    'WF_003': 'invalidWorkflowConfiguration',
    
    // Network codes
    'NETWORK_ERROR': 'networkError',
    'SERVER_ERROR': 'serverError',
    'REQUEST_TIMEOUT': 'requestTimeout',
    
    // System codes
    'SYS_001': 'serverError',
    'SYS_002': 'serviceUnavailable'
  };
  
  const messageKey = codeMapping[errorCode];
  if (messageKey) {
    return getErrorMessage(messageKey, defaultMessage, language);
  }
  
  return defaultMessage || getErrorMessage('unexpectedError', null, language);
};

/**
 * Get current language
 * @returns {string} Current language code
 */
export const getCurrentLanguage = () => currentLanguage;

/**
 * Get all available languages
 * @returns {string[]} Array of available language codes
 */
export const getAvailableLanguages = () => Object.keys(errorMessages);

export default errorMessages;